import type { FormEvent } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Grid } from '@material-ui/core';

import { ActionButton, Block, Heading } from '@klp/design';
import { ButtonsContainer } from '@klp/design/ButtonsContainer';
import type { ITableConsumerState, TableColumnValue } from '@klp/design/Table';
import { Table, TableColumnDef } from '@klp/design/Table';
import { useKlpStyles } from '@klp/design/Theme';
import { MimeTypes } from '@klp/file';
import {
    Form,
    FormButton,
    FormCheckbox,
    FormDropdown,
    FormEditor,
    FormFiles,
    FormInput,
    FormValidator,
} from '@klp/form';

import { supportFormApiRoutes } from '../api';
import {
    KLPSupportForm,
    governmentSupportMonths,
    revenueMonths,
    shoppingCenters,
} from '../classes/KLPSupportForm';
import type {
    IGovernmentSupportMonth,
    IKLPSupportForm,
    IRevenueMonth,
} from '../classes/KLPSupportForm';

const styles = () => ({
    form: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        margin: '1em 0',
        '& > *': {
            marginBottom: '2em !important',
            flexBasis: 'auto',
        },
    },
});

interface IProps {
    klpSupportForm: KLPSupportForm;
    onSubmit: (
        event: FormEvent<HTMLFormElement>,
        values: KLPSupportForm,
    ) => void;
    onCancel: () => void;
}

export type ISupportApplicationForm = IProps;

export const KlpSupportFormForm: React.FC<ISupportApplicationForm> = (
    props,
) => {
    const { t } = useTranslation();
    const classes = useKlpStyles({}, styles);
    const { klpSupportForm, onSubmit, onCancel } = props;

    const handleSubmit = (
        event: FormEvent<HTMLFormElement>,
        values: IKLPSupportForm,
    ) => {
        event.preventDefault();

        if (onSubmit) {
            onSubmit(event, KLPSupportForm.parseObject(values));
        }
    };

    const handleCancel = () => {
        if (onCancel) {
            onCancel();
        }
    };

    // Helper to render required text inputs with standard layout
    const renderFormInput = (
        name: keyof KLPSupportForm,
        titleKey: string,
        helperTextKey: string,
        _dataCy: string,
    ) => (
        <Grid item={true} xs={12}>
            <Grid container={true}>
                <Grid item={true} xs={12} sm={10} md={8} lg={6}>
                    <FormInput
                        name={name}
                        title={t(titleKey)}
                        validators={[
                            new FormValidator(
                                FormValidator.validations.required,
                                t('validator.required'),
                            ),
                        ]}
                        value={klpSupportForm[name] as string}
                        helperText={t(helperTextKey)}
                        required={true}
                    />
                </Grid>
            </Grid>
        </Grid>
    );

    const renderGovernmentSupportCaseNumber = <DataType, FilterType>(
        _value: TableColumnValue,
        _column: TableColumnDef<DataType, FilterType>,
        _context: ITableConsumerState<DataType, FilterType>,
        row: IGovernmentSupportMonth,
    ) => {
        return (
            <FormInput
                name={`governmentSupportCaseNumber2020${row.monthNumber}`}
                title={t('supportForm.governmentSupport.caseNumber')}
                validators={[
                    new FormValidator(
                        FormValidator.validations.required,
                        t('validator.required'),
                    ),
                ]}
                value={
                    klpSupportForm[
                        `governmentSupportCaseNumber2020${row.monthNumber}`
                    ]
                }
                helperText={t(
                    'supportForm.governmentSupport.caseNumberHelperText',
                )}
                required={true}
            />
        );
    };

    const renderGovernmentSupportAmount = <DataType, FilterType>(
        _value: TableColumnValue,
        _column: TableColumnDef<DataType, FilterType>,
        _context: ITableConsumerState<DataType, FilterType>,
        row: IGovernmentSupportMonth,
    ) => {
        return (
            <FormInput
                name={`governmentSupportAmount2020${row.monthNumber}`}
                title={t('supportForm.governmentSupport.amount')}
                validators={[
                    new FormValidator(
                        FormValidator.validations.required,
                        t('validator.required'),
                    ),
                ]}
                value={
                    klpSupportForm[
                        `governmentSupportAmount2020${row.monthNumber}`
                    ]
                }
                helperText={t('supportForm.governmentSupport.amountHelperText')}
                required={true}
            />
        );
    };

    const renderRevenue =
        (year: number) =>
        <DataType, FilterType>(
            _value: TableColumnValue,
            _column: TableColumnDef<DataType, FilterType>,
            _context: ITableConsumerState<DataType, FilterType>,
            row: IRevenueMonth,
        ) => {
            return (
                <FormInput
                    name={`revenue${year}${row.monthNumber}`}
                    title={t('supportForm.revenue.amount')}
                    validators={[
                        new FormValidator(
                            FormValidator.validations.required,
                            t('validator.required'),
                        ),
                    ]}
                    value={klpSupportForm[`revenue${year}${row.monthNumber}`]}
                    helperText={t('supportForm.revenue.amountHelperText')}
                    required={true}
                />
            );
        };

    const {
        hasGovernmentSupport,
        governmentSupportExplanation,
        klpSupportReason,
        shoppingCenter,
        supportFormFiles,
    } = klpSupportForm;

    return (
        <Form
            className={classes.form}
            onSubmit={handleSubmit}
            values={{ ...klpSupportForm }}
        >
            <Grid container={true}>
                <Grid item={true} xs={12}>
                    <Heading
                        klpHeadingLevel="ONE"
                        translation="supportForm.title"
                    />
                </Grid>
                <Grid item={true} xs={12}>
                    <Heading
                        klpHeadingLevel="THREE"
                        translation="supportForm.contact"
                        marginTop="bulky"
                        marginBottom="bulky"
                    />
                </Grid>

                {renderFormInput(
                    'email',
                    'supportForm.email.email',
                    'supportForm.email.emailHelperText',
                    'SupportForm__Email',
                )}
                {renderFormInput(
                    'phoneNumber',
                    'supportForm.phoneNumber.phoneNumber',
                    'supportForm.phoneNumber.phoneNumberHelperText',
                    'SupportForm__PhoneNumber',
                )}
                {renderFormInput(
                    'personApplying',
                    'supportForm.personApplying.personName',
                    'supportForm.personApplying.personNameHelperText',
                    'SupportForm__PersonApplying',
                )}

                <Grid item={true} xs={12}>
                    <Heading
                        klpHeadingLevel="THREE"
                        translation="supportForm.leietakerinformasjon"
                        marginTop="jumbo"
                        marginBottom="bulky"
                    />
                </Grid>

                {renderFormInput(
                    'companyName',
                    'supportForm.company.companyName',
                    'supportForm.company.companyNameHelperText',
                    'SupportForm__CompanyName',
                )}
                {renderFormInput(
                    'storeName',
                    'supportForm.store.storeName',
                    'supportForm.store.storeNameHelperText',
                    'SupportForm__StoreName',
                )}
                {renderFormInput(
                    'orgNumber',
                    'supportForm.company.orgNumber',
                    'supportForm.company.orgNumberHelperText',
                    'SupportForm__OrgNumber',
                )}
                {renderFormInput(
                    'kundeNummer',
                    'supportForm.kundeNummer.clientNumber',
                    'supportForm.kundeNummer.clientNumberHelperText',
                    'SupportForm__ClientNumber',
                )}
                {renderFormInput(
                    'invoiceNumber',
                    'supportForm.invoic2020Q3.invoiceNumber',
                    'supportForm.invoice2020Q3.invoiceNumberHelperText',
                    'SupportForm__InvoiceNumber',
                )}

                <Grid item={true} xs={12}>
                    <Grid container={true}>
                        <Grid item={true} xs={12} sm={10} md={8} lg={6}>
                            <FormDropdown
                                name="shoppingCenter"
                                title={t(
                                    'supportForm.shoppingCenter.shoppingCenterName',
                                )}
                                value={shoppingCenter}
                                items={shoppingCenters.map((item) => ({
                                    label: item,
                                    value: item,
                                }))}
                                validators={[
                                    new FormValidator(
                                        FormValidator.validations.required,
                                        t('validator.required'),
                                    ),
                                ]}
                                helperText={t(
                                    'supportForm.shoppingCenter.shoppingCenterHelperText',
                                )}
                                required={true}
                            />
                        </Grid>
                    </Grid>
                </Grid>

                <Grid item={true} xs={12}>
                    <Heading
                        klpHeadingLevel="THREE"
                        translation="supportForm.support"
                        marginTop="jumbo"
                        marginBottom="bulky"
                    />
                </Grid>

                <Grid item={true} xs={12}>
                    <FormCheckbox
                        name="hasGovernmentSupport"
                        title={t(
                            'supportForm.governmentSupport.governmentSupport',
                        )}
                        value={hasGovernmentSupport}
                        helperText={t(
                            'supportForm.governmentSupport.governmentSupportHelperText',
                        )}
                    />
                </Grid>

                <Grid item={true} xs={12}>
                    <Grid container={true}>
                        <Grid item={true} xs={12} sm={10} md={8} lg={6}>
                            <FormEditor
                                name="governmentSupportExplanation"
                                title={t(
                                    'supportForm.governmentSupport.governmentSupportExplanation',
                                )}
                                value={governmentSupportExplanation}
                                helperText={t(
                                    'supportForm.governmentSupport.governmentSupportExplanationHelperText',
                                )}
                            />
                        </Grid>
                    </Grid>
                </Grid>

                <Grid item={true} xs={12}>
                    <Heading
                        klpHeadingLevel="THREE"
                        translation="supportForm.economy"
                        marginTop="jumbo"
                        marginBottom="bulky"
                    />
                </Grid>

                <Grid item={true} xs={12}>
                    <Grid container={true}>
                        <Grid item={true} xs={12} sm={10} md={8} lg={6}>
                            <Table
                                id="SupportFormGovernmentSupportMonths"
                                data={governmentSupportMonths}
                                columns={[
                                    new TableColumnDef({
                                        key: 'month',
                                        translation:
                                            'supportForm.governmentSupport.month',
                                    }),
                                    new TableColumnDef({
                                        key: 'caseNumber',
                                        translation:
                                            'supportForm.governmentSupport.caseNumber',
                                        render: renderGovernmentSupportCaseNumber,
                                    }),
                                    new TableColumnDef({
                                        key: 'supportSum',
                                        translation:
                                            'supportForm.governmentSupport.amount',
                                        render: renderGovernmentSupportAmount,
                                    }),
                                ]}
                            />
                        </Grid>
                    </Grid>
                </Grid>

                <Grid item={true} xs={12}>
                    <Heading
                        klpHeadingLevel="THREE"
                        translation="supportForm.revenue.title"
                        marginTop="jumbo"
                        marginBottom="bulky"
                    />
                </Grid>

                <Grid item={true} xs={12}>
                    <Grid container={true}>
                        <Grid item={true} xs={12} sm={10} md={8} lg={6}>
                            <Table<IRevenueMonth, unknown>
                                id="SupportFormRevenueMonths"
                                data={revenueMonths}
                                columns={[
                                    new TableColumnDef<IRevenueMonth, unknown>({
                                        key: 'month',
                                        translation:
                                            'supportForm.revenue.month',
                                    }),
                                    new TableColumnDef<IRevenueMonth, unknown>({
                                        key: 'revenue2019',
                                        translation:
                                            'supportForm.revenue.revenue2019',
                                        render: renderRevenue(2019),
                                    }),
                                    new TableColumnDef<IRevenueMonth, unknown>({
                                        key: 'revenue2020',
                                        translation:
                                            'supportForm.revenue.revenue2020',
                                        render: renderRevenue(2020),
                                    }),
                                ]}
                            />
                        </Grid>
                    </Grid>
                </Grid>

                <Grid item={true} xs={12}>
                    <Heading
                        klpHeadingLevel="THREE"
                        translation="supportForm.attachments.title"
                        marginTop="jumbo"
                        marginBottom="bulky"
                    />
                </Grid>

                <Grid item={true} xs={12}>
                    <FormFiles
                        name="supportFormFiles"
                        title={t('supportForm.attachments.attachments')}
                        value={
                            Array.isArray(supportFormFiles)
                                ? supportFormFiles
                                : []
                        }
                        mimeTypes={MimeTypes.document}
                        path={supportFormApiRoutes.formFiles('id')}
                        helperText={t(
                            'supportForm.attachments.attachmentsHelperText',
                        )}
                        accept={MimeTypes.document}
                        queueName="supportFormFiles"
                    />
                </Grid>

                <Grid item={true} xs={12}>
                    <Heading
                        klpHeadingLevel="THREE"
                        translation="supportForm.other.title"
                        marginTop="jumbo"
                        marginBottom="bulky"
                    />
                </Grid>
                <Grid item={true} xs={12}>
                    <FormEditor
                        name="klpSupportReason"
                        title={t('supportForm.other.klpSupportReasonTitle')}
                        value={klpSupportReason}
                        helperText={t(
                            'supportForm.other.klpSupportReasonHelperText',
                        )}
                    />
                </Grid>

                <Grid item={true} xs={12}>
                    <Block marginTop="jumbo" marginBottom="bulky">
                        <Grid container={true} justifyContent="flex-start">
                            <Grid item={true} xs={12}>
                                <ButtonsContainer>
                                    <ActionButton
                                        actionType="cancel"
                                        translation="general.cancel"
                                        onClick={handleCancel}
                                    />

                                    <FormButton
                                        name="Submit"
                                        actionType="submit"
                                        translation="general.save"
                                        disableOnError={true}
                                    />
                                </ButtonsContainer>
                            </Grid>
                        </Grid>
                    </Block>
                </Grid>
            </Grid>
        </Form>
    );
};
